(() => {
  'use strict';
  const SERVICE_PRICE = 300000; // Shopify monetary units: JPY 3,000.

  function buildPayload(formData, serviceId, title, group) {
    const id = Number(formData.get('id'));
    const rawQuantity = formData.get('quantity');
    const quantity = rawQuantity == null ? 1 : Number(rawQuantity);
    if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(serviceId) || serviceId <= 0 || id === serviceId) {
      throw new Error('商品を選択してください。');
    }
    if (!Number.isSafeInteger(quantity) || quantity < 1) throw new Error('数量を確認してください。');
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match) {
        if (typeof value !== 'string') throw new Error('このオプションはファイル添付と併用できません。');
        properties[match[1]] = value;
      }
    }
    properties._kestore_metate_group = group;
    const parent = { id, quantity, properties };
    const plan = formData.get('selling_plan');
    if (plan) parent.selling_plan = plan;
    return { items: [parent, {
      id: serviceId, quantity, parent_id: id,
      properties: {
        _kestore_metate_group: group,
        _kestore_metate_addon: '1',
        '対象チェンソー': title
      }
    }] };
  }

  function validateService(product, serviceId) {
    const variant = product.variants?.find(v => Number(v.id) === serviceId);
    if (!variant?.available || Number(variant.price) !== SERVICE_PRICE) {
      throw new Error('目立てサービスを追加できません。価格・販売状況をご確認ください。');
    }
    return variant;
  }

  function validateAdded(response, payload) {
    const group = payload.items[0].properties._kestore_metate_group;
    const parent = response.items?.find(i => Number(i.variant_id ?? i.id) === payload.items[0].id && i.properties?._kestore_metate_group === group);
    const child = response.items?.find(i => Number(i.variant_id ?? i.id) === payload.items[1].id && i.properties?._kestore_metate_group === group);
    return !!(parent && child && parent.quantity === payload.items[0].quantity && child.quantity === payload.items[1].quantity && child.parent_relationship?.parent_key === parent.key);
  }

  // Pure helpers are exposed for the local verification harness.
  globalThis.KestoreMetate = Object.freeze({ buildPayload, validateService, validateAdded });
  if (typeof document === 'undefined') return;

  const root = () => window.Shopify?.routes?.root || '/';
  const busyForms = new WeakSet();
  const yen = value => new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(value / 100);

  function updateTotal(widget) {
    const form = widget.closest('form');
    if (!form) return;
    const variants = JSON.parse(widget.querySelector('[data-kestore-metate-variants]').textContent);
    const id = new FormData(form).get('id');
    const variant = variants.find(v => String(v.id) === String(id));
    const total = widget.querySelector('[data-kestore-metate-total]');
    if (!variant || widget.querySelector('select').value !== 'yes') { total.textContent = ''; return; }
    const input = form.querySelector('[name="quantity"]');
    const quantity = Number(input?.value || 1);
    total.textContent = `本体＋目立て：${yen(Number(variant.price) + SERVICE_PRICE)}／台（税込）`;
    if (Number.isSafeInteger(quantity) && quantity > 1) total.textContent += `　${quantity}台合計：${yen((Number(variant.price) + SERVICE_PRICE) * quantity)}`;
  }

  function initialize(scope) {
    scope.querySelectorAll('[data-kestore-metate]').forEach(widget => {
      if (widget.dataset.initialized) return;
      widget.dataset.initialized = 'true';
      if (widget.dataset.serviceReady === 'true') widget.querySelector('option[value="yes"]').disabled = false;
      updateTotal(widget);
    });
  }

  // Capture before Empire's normal AJAX/flyout or high-variant form handler.
  // No-service submissions pass through completely unchanged.
  window.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const widget = form.querySelector('[data-kestore-metate]');
    if (!widget || widget.querySelector('select').value !== 'yes') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busyForms.has(form)) return;
    const errorBox = widget.querySelector('[data-kestore-metate-error]');
    errorBox.hidden = true;
    if (widget.dataset.serviceReady !== 'true' || window.Shopify?.currency?.active && window.Shopify.currency.active !== 'JPY') {
      errorBox.textContent = '現在、このオプションは選択できません。'; errorBox.hidden = false; return;
    }
    busyForms.add(form);
    const buttons = [...form.querySelectorAll('button[type="submit"], input[type="submit"]')];
    const states = buttons.map(b => b.disabled);
    buttons.forEach(b => { b.disabled = true; });
    form.setAttribute('aria-busy', 'true');
    let requestSent = false;
    try {
      const serviceId = Number(widget.dataset.serviceId);
      const serviceResponse = await fetch(`${root()}products/${encodeURIComponent(widget.dataset.serviceHandle)}.js`, { credentials: 'same-origin' });
      if (!serviceResponse.ok) throw new Error('目立てサービスの販売状況を確認できません。');
      validateService(await serviceResponse.json(), serviceId);
      const group = window.crypto.randomUUID();
      const payload = buildPayload(new FormData(form), serviceId, widget.dataset.productTitle, group);
      requestSent = true;
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'カートへの追加に失敗しました。');
      if (!validateAdded(result, payload)) throw new Error('カートの追加内容を確認してください。');
      window.location.assign(`${root()}cart`);
    } catch (error) {
      errorBox.textContent = (error.message || '追加処理を完了できませんでした。') + (requestSent ? ' 再度追加する前に、カート内の本体・サービス・数量を確認してください。' : '');
      errorBox.hidden = false;
      errorBox.focus?.();
    } finally {
      buttons.forEach((b, i) => { b.disabled = states[i]; });
      form.removeAttribute('aria-busy');
      busyForms.delete(form);
    }
  }, true);

  document.addEventListener('change', event => {
    const form = event.target.closest('form');
    if (form) setTimeout(() => form.querySelectorAll('[data-kestore-metate]').forEach(updateTotal), 0);
  });
  document.addEventListener('input', event => {
    const form = event.target.closest('form');
    if (form) form.querySelectorAll('[data-kestore-metate]').forEach(updateTotal);
  });
  initialize(document);
  new MutationObserver(() => initialize(document)).observe(document.body, { childList: true, subtree: true });
})();
